import React, { useState, useContext, createContext } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

// Toast Context for global state management
const ToastContext = createContext();

// Hook to use toast notifications
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Toast Provider
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const MAX_TOASTS = 3;

  const addToast = (message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      message,
      type,
      timestamp: Date.now()
    };

    setToasts(prev => {
      const newToasts = [toast, ...prev];
      return newToasts.slice(0, MAX_TOASTS);
    });

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const clearAllToasts = () => {
    setToasts([]);
  };

  return (
    <ToastContext.Provider value={{
      addToast,
      removeToast,
      clearAllToasts
    }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// Individual Toast Component with CSS styling
const Toast = ({ toast, onRemove, index }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleRemove = () => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 200);
  };

  const getToastStyles = () => {
    const baseStyles = {
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      minHeight: '60px',
      width: '400px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151'
    };

    switch (toast.type) {
      case 'success':
        return {
          ...baseStyles,
          borderLeft: '4px solid #10b981'
        };
      case 'error':
        return {
          ...baseStyles,
          borderLeft: '4px solid #ef4444'
        };
      case 'info':
      default:
        return {
          ...baseStyles,
          borderLeft: '4px solid #3b82f6'
        };
    }
  };

  const getIcon = () => {
    const iconProps = { 
      style: { 
        width: '20px', 
        height: '20px', 
        flexShrink: 0,
        marginTop: '2px'
      } 
    };
    
    switch (toast.type) {
      case 'success':
        return <CheckCircle {...iconProps} style={{...iconProps.style, color: '#10b981'}} />;
      case 'error':
        return <AlertCircle {...iconProps} style={{...iconProps.style, color: '#ef4444'}} />;
      case 'info':
      default:
        return <Info {...iconProps} style={{...iconProps.style, color: '#3b82f6'}} />;
    }
  };

  const stackOffset = index * 12;
  const scaleDown = 1 - (index * 0.05);
  const opacity = isExiting ? 0 : (1 - (index * 0.15));

  const toastStyle = {
    ...getToastStyles(),
    transform: `translateY(${stackOffset}px) scale(${scaleDown})`,
    opacity: opacity,
    zIndex: 1000 - index,
    transformOrigin: 'top center',
    position: index > 0 ? 'absolute' : 'relative',
    top: index > 0 ? 0 : 'auto',
    left: index > 0 ? '50%' : 'auto',
    marginLeft: index > 0 ? '-200px' : 'auto',
    transition: isExiting ? 'all 0.2s ease-out' : 'all 0.3s ease-out',
    animation: !isExiting ? 'slideIn 0.3s ease-out' : undefined
  };

  return (
    <div style={toastStyle}>
      {getIcon()}
      
      <div style={{ 
        flex: 1, 
        minWidth: 0,
        lineHeight: '1.4',
        wordWrap: 'break-word'
      }}>
        {toast.message}
      </div>
      
      <button
        onClick={handleRemove}
        style={{
          flexShrink: 0,
          padding: '4px',
          borderRadius: '6px',
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#f3f4f6';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'transparent';
        }}
      >
        <X style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
      </button>
    </div>
  );
};

// Toast Container with CSS positioning
const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      <div style={{
        position: 'fixed',
        top: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        pointerEvents: 'none'
      }}>
        <div style={{ 
          position: 'relative', 
          pointerEvents: 'auto' 
        }}>
          {toasts.map((toast, index) => (
            <Toast
              key={toast.id}
              toast={toast}
              onRemove={onRemove}
              index={index}
            />
          ))}
        </div>
      </div>
    </>
  );
};
